/**
 * 「팀원 일정도 보기」 — 서버가 덧붙여 준 «팀원 일정»을 캘린더 체크박스 거르기에서 살려 둔다 (2026-10-13).
 *
 * ## 왜 필요한가
 *   그리드는 받은 일정을 `filterByVisible`(lib/calendar-visibility)로 «내 캘린더 체크박스» 기준으로 거른다.
 *   그런데 팀원 일정은 대개 **팀원의 개인 캘린더**에 있어서 내 체크박스 목록에 «그 칸이 없다»
 *   ⇒ 그냥 거르면 서버가 준 팀원 일정이 «하나도» 안 남는다(켜도 아무 일이 안 일어난다).
 *
 * ## 무엇을 살리나 — «팀원 확장으로만» 받은 일정
 *   · 내가 만든 것이 아니고
 *   · 내가 참석자도 아니고
 *   · 내 캘린더 목록(`knownCalendarIds`)에 «없는» 캘린더에 있거나 캘린더가 없는 것
 *   ⇒ 내 일정·내 팀 캘린더 일정은 종전처럼 체크박스를 «그대로» 따른다(꺼 둔 캘린더는 계속 숨는다).
 *
 * ⚠ 꺼져 있으면(enabled=false) `filtered` 를 그대로 돌려준다 — 종전과 같다.
 * ⚠ 차례는 서버가 준 차례(`got`)를 지킨다.
 */
export interface TeamMemberMergeEvent {
  creatorId: string;
  calendarId?: string | null;
  attendees: ReadonlyArray<{ userId: string }>;
}

export function isTeamMemberOnlyEvent(
  e: TeamMemberMergeEvent,
  myUserId: string | null,
  knownCalendarIds: ReadonlySet<string>,
): boolean {
  if (myUserId === null) return false;           // 내가 누군지 모르면 넘겨짚지 않는다
  if (e.creatorId === myUserId) return false;
  if (e.attendees.some((a) => a.userId === myUserId)) return false;
  return e.calendarId == null || !knownCalendarIds.has(e.calendarId);
}

export function mergeTeamMemberEvents<T extends TeamMemberMergeEvent>(
  got: readonly T[],
  filtered: readonly T[],
  opts: { enabled: boolean; myUserId: string | null; knownCalendarIds: Iterable<string> },
): T[] {
  if (!opts.enabled) return [...filtered];
  const keep = new Set<T>(filtered);
  const known = new Set(opts.knownCalendarIds);
  return got.filter((e) => keep.has(e) || isTeamMemberOnlyEvent(e, opts.myUserId, known));
}
